import { FC, useMemo } from 'react';
import styled from 'styled-components';
import { useInfiniteHits, UseInfiniteHitsProps } from 'react-instantsearch';
import { ICON_NAMES } from '@belong/types';
import { Button, ButtonGroup, Copy, Heading, Icon, Inline, Stack } from '@belong/ui-components';
import { SearchHitGroup } from './SearchHitGroup';
import { SEARCH_TEST_IDS } from './test/utils/testIds';
import type { BaseHit, Hit } from 'instantsearch.js';

const SearchHeading = styled(Heading)`
  overflow-wrap: break-word;
`;

export const SearchHits: FC<UseInfiniteHitsProps> = () => {
  const { items, results, showMore, isLastPage } = useInfiniteHits();
  const resultsPerPage = results?.hitsPerPage ?? 0;

  const groupedHits = useMemo(() => {
    const hitsArray: Hit<BaseHit>[][] = [];
    for (let i = 0; i < items.length; i += resultsPerPage) {
      hitsArray.push(items.slice(i, i + resultsPerPage));
    }

    return hitsArray;
  }, [items, resultsPerPage]);

  // Focus on the header of the new results group when user clicks on 'see more results' (a11y req)
  const focusOnResultsGroupHeading = () => {
    const pageNumber = Math.ceil(items.length / resultsPerPage) + 1;

    const groupHeading = document.getElementById(
      `${SEARCH_TEST_IDS.RESULTS_GROUP_HEADING}-${pageNumber}`
    ) as HTMLHeadingElement;

    if (groupHeading) {
      groupHeading.focus();
    }
  };

  return (
    <Stack space={{ xs: 'large', md: 'xlarge' }}>
      <SearchHeading alignment="center" as="h2" variant="large" data-testid={SEARCH_TEST_IDS.RESULTS_HEADER}>
        {`${results?.nbHits || 0} search results for “${results?.query || ''}”`}
      </SearchHeading>
      {groupedHits.map((group, idx) => (
        <SearchHitGroup
          key={group.length ? `group-${group[0].objectID}-${group[group.length - 1].objectID}` : 'group-empty'}
          hits={group}
          start={idx === 0 ? idx + 1 : idx * resultsPerPage + 1}
          end={
            idx * resultsPerPage + resultsPerPage > items.length ? items.length : idx * resultsPerPage + resultsPerPage
          }
          page={idx + 1}
        />
      ))}

      {isLastPage && items.length > 0 ? (
        <Copy isBold alignment="center" variant="large">
          End of results
        </Copy>
      ) : (
        <ButtonGroup hasControl={{ xs: ['column', 'center', false] }}>
          <Button
            data-testid={SEARCH_TEST_IDS.RESULTS_SEE_MORE_RESULTS_BUTTON}
            onClick={() => {
              showMore();
              setTimeout(() => {
                focusOnResultsGroupHeading();
              }, 500);
            }}
            variant="secondary"
          >
            <Inline space="xxsmall" alignY="center">
              See more results
              <Icon name={ICON_NAMES.ChevronDown} />
            </Inline>
          </Button>
        </ButtonGroup>
      )}
    </Stack>
  );
};

SearchHits.displayName = 'SearchHits';
